import { useState, type ReactNode, useRef } from 'react';
import { Button, Card, Input, Select, Modal, LoadingSkeleton, Badge } from '@nexora/design-system';
import { useToast } from '@nexora/design-system';
import {
  useDocuments,
  useDocument,
  useUploadDocument,
  useUpdateDocument,
  useDeleteDocument,
  useUploadVersion,
  type Document,
  type DocumentUpdateInput,
} from './useDocuments';
import { formatDate } from '../../shared/utils/format';

/* ── Inline SVG Icons (lucide-react not available) ── */

function IconFileText({ size = 24 }: { size?: number }): JSX.Element {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z" />
      <polyline points="14 2 14 8 20 8" />
      <line x1="16" y1="13" x2="8" y2="13" />
      <line x1="16" y1="17" x2="8" y2="17" />
      <polyline points="10 9 9 9 8 9" />
    </svg>
  );
}

function IconFileImage({ size = 24 }: { size?: number }): JSX.Element {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z" />
      <polyline points="14 2 14 8 20 8" />
      <circle cx="10" cy="13" r="2" />
      <path d="m20 17-1.09-1.09a2 2 0 0 0-2.82 0L10 22" />
    </svg>
  );
}

function IconCode({ size = 24 }: { size?: number }): JSX.Element {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="16 18 22 12 16 6" />
      <polyline points="8 6 2 12 8 18" />
    </svg>
  );
}

function IconDownload({ size = 18 }: { size?: number }): JSX.Element {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
      <polyline points="7 10 12 15 17 10" />
      <line x1="12" y1="15" x2="12" y2="3" />
    </svg>
  );
}

function IconUpload({ size = 18 }: { size?: number }): JSX.Element {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
      <polyline points="17 8 12 3 7 8" />
      <line x1="12" y1="3" x2="12" y2="15" />
    </svg>
  );
}

function IconTrash2({ size = 18 }: { size?: number }): JSX.Element {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 6h18" />
      <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6" />
      <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2" />
      <line x1="10" y1="11" x2="10" y2="17" />
      <line x1="14" y1="11" x2="14" y2="17" />
    </svg>
  );
}

function IconLink({ size = 18 }: { size?: number }): JSX.Element {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
      <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
    </svg>
  );
}

/* ── Helpers ── */

function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB'];
  const k = 1024;
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  const value = bytes / Math.pow(k, i);
  return `${value.toFixed(i === 0 ? 0 : 1)} ${units[i]}`;
}

function formatDateTime(dateStr: string): string {
  return new Date(dateStr).toLocaleString('pt-BR');
}

function getFileIcon(mimeType: string, size = 32): JSX.Element {
  if (mimeType.startsWith('image/')) return <IconFileImage size={size} />;
  if (mimeType.includes('xml') || mimeType.includes('json') || mimeType.includes('html')) {
    return <IconCode size={size} />;
  }
  return <IconFileText size={size} />;
}

const categoryOptions = [
  { value: 'ALL', label: 'Todas as categorias' },
  { value: 'FISCAL', label: 'Fiscal' },
  { value: 'COMMERCIAL', label: 'Comercial' },
  { value: 'FINANCIAL', label: 'Financeiro' },
  { value: 'CONTRACT', label: 'Contratos' },
  { value: 'HR', label: 'RH' },
  { value: 'LEGAL', label: 'Juridico' },
  { value: 'TECHNICAL', label: 'Tecnico' },
  { value: 'OTHER', label: 'Outros' },
];

const categoryLabel: Record<string, string> = {
  FISCAL: 'Fiscal',
  COMMERCIAL: 'Comercial',
  FINANCIAL: 'Financeiro',
  CONTRACT: 'Contratos',
  HR: 'RH',
  LEGAL: 'Juridico',
  TECHNICAL: 'Tecnico',
  OTHER: 'Outros',
};

const categoryColors: Record<string, 'info' | 'success' | 'warning' | 'error' | 'neutral'> = {
  FISCAL: 'info',
  COMMERCIAL: 'success',
  FINANCIAL: 'warning',
  CONTRACT: 'neutral',
  HR: 'neutral',
  LEGAL: 'error',
  TECHNICAL: 'info',
  OTHER: 'neutral',
};

/* ── Upload Modal Form Data ── */

interface UploadFormData {
  name: string;
  category: string;
  tags: string;
  companyId: string;
  file: File | null;
}

const emptyUploadForm: UploadFormData = {
  name: '',
  category: '',
  tags: '',
  companyId: '',
  file: null,
};

/* ── Page Component ── */

export function DocumentsPage(): JSX.Element {
  const { addToast } = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const versionFileInputRef = useRef<HTMLInputElement>(null);

  /* ── List state ── */
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('ALL');
  const pageSize = 20;

  /* ── Detail modal ── */
  const [detailDocId, setDetailDocId] = useState<string | null>(null);

  /* ── Upload modal ── */
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [uploadForm, setUploadForm] = useState<UploadFormData>(emptyUploadForm);
  const [uploadErrors, setUploadErrors] = useState<string[]>([]);
  const [isUploading, setIsUploading] = useState(false);

  /* ── Edit modal ── */
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingDoc, setEditingDoc] = useState<Document | null>(null);
  const [editName, setEditName] = useState('');
  const [editCategory, setEditCategory] = useState('');
  const [editTags, setEditTags] = useState('');
  const [editErrors, setEditErrors] = useState<string[]>([]);
  const [isSavingEdit, setIsSavingEdit] = useState(false);

  /* ── Delete confirm ── */
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);

  /* ── New version modal ── */
  const [versionModalOpen, setVersionModalOpen] = useState(false);
  const [versionDocId, setVersionDocId] = useState<string | null>(null);
  const [versionFile, setVersionFile] = useState<File | null>(null);
  const [versionNotes, setVersionNotes] = useState('');
  const [versionErrors, setVersionErrors] = useState<string[]>([]);
  const [isUploadingVersion, setIsUploadingVersion] = useState(false);

  /* ── Queries ── */
  const { data, isLoading, error } = useDocuments({
    search: search || undefined,
    category: categoryFilter !== 'ALL' ? categoryFilter : undefined,
    page,
    limit: pageSize,
  });

  const { data: detailDoc, isLoading: detailLoading } = useDocument(detailDocId ?? '');

  /* ── Mutations ── */
  const uploadDoc = useUploadDocument();
  const updateDoc = useUpdateDocument();
  const deleteDoc = useDeleteDocument();
  const uploadVersion = useUploadVersion();

  const documents = data?.data ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.ceil(total / pageSize);

  /* ── Upload handlers ── */

  const openUploadModal = (): void => {
    setUploadForm(emptyUploadForm);
    setUploadErrors([]);
    setUploadModalOpen(true);
  };

  const closeUploadModal = (): void => {
    setUploadModalOpen(false);
    setUploadForm(emptyUploadForm);
    setUploadErrors([]);
  };

  const validateUploadForm = (): boolean => {
    const errors: string[] = [];
    if (!uploadForm.name.trim()) errors.push('Informe o nome do documento');
    if (!uploadForm.category) errors.push('Selecione uma categoria');
    if (!uploadForm.file) errors.push('Selecione um arquivo');
    setUploadErrors(errors);
    return errors.length === 0;
  };

  const handleUpload = async (): Promise<void> => {
    if (!validateUploadForm()) return;
    setIsUploading(true);

    try {
      const formData = new FormData();
      if (uploadForm.file) formData.append('file', uploadForm.file);
      formData.append('name', uploadForm.name.trim());
      formData.append('category', uploadForm.category);
      if (uploadForm.tags.trim()) {
        formData.append('tags', uploadForm.tags.trim());
      }
      if (uploadForm.companyId.trim()) {
        formData.append('companyId', uploadForm.companyId.trim());
      }

      await uploadDoc.mutateAsync(formData);
      addToast('Documento enviado com sucesso', 'success');
      closeUploadModal();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Erro ao fazer upload';
      setUploadErrors([message]);
      addToast(message, 'error');
    } finally {
      setIsUploading(false);
    }
  };

  /* ── Edit handlers ── */

  const openEditModal = (doc: Document): void => {
    setEditingDoc(doc);
    setEditName(doc.title);
    setEditCategory(doc.category);
    setEditTags(doc.tags.join(', '));
    setEditErrors([]);
    setEditModalOpen(true);
  };

  const closeEditModal = (): void => {
    setEditModalOpen(false);
    setEditingDoc(null);
    setEditName('');
    setEditCategory('');
    setEditTags('');
    setEditErrors([]);
  };

  const handleEditSave = async (): Promise<void> => {
    if (!editingDoc) return;
    const errors: string[] = [];
    if (!editName.trim()) errors.push('Informe o nome do documento');
    if (!editCategory) errors.push('Selecione uma categoria');
    setEditErrors(errors);
    if (errors.length > 0) return;

    setIsSavingEdit(true);
    try {
      const input: DocumentUpdateInput = {
        name: editName.trim(),
        category: editCategory,
        tags: editTags
          .split(',')
          .map((t) => t.trim())
          .filter(Boolean),
      };
      await updateDoc.mutateAsync({ id: editingDoc.id, input });
      addToast('Documento atualizado com sucesso', 'success');
      closeEditModal();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Erro ao atualizar';
      setEditErrors([message]);
      addToast(message, 'error');
    } finally {
      setIsSavingEdit(false);
    }
  };

  /* ── Delete handler ── */

  const handleDelete = async (id: string): Promise<void> => {
    try {
      await deleteDoc.mutateAsync(id);
      addToast('Documento excluido com sucesso', 'success');
      setDeleteConfirmId(null);
      if (detailDocId === id) setDetailDocId(null);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Erro ao excluir';
      addToast(message, 'error');
    }
  };

  /* ── New version handlers ── */

  const openVersionModal = (docId: string): void => {
    setVersionDocId(docId);
    setVersionFile(null);
    setVersionNotes('');
    setVersionErrors([]);
    setVersionModalOpen(true);
  };

  const closeVersionModal = (): void => {
    setVersionModalOpen(false);
    setVersionDocId(null);
    setVersionFile(null);
    setVersionNotes('');
    setVersionErrors([]);
  };

  const handleUploadVersion = async (): Promise<void> => {
    if (!versionDocId) return;
    const errors: string[] = [];
    if (!versionFile) errors.push('Selecione um arquivo');
    setVersionErrors(errors);
    if (errors.length > 0) return;

    setIsUploadingVersion(true);
    try {
      const formData = new FormData();
      if (versionFile) formData.append('file', versionFile);
      if (versionNotes.trim()) formData.append('notes', versionNotes.trim());

      await uploadVersion.mutateAsync({ id: versionDocId, formData });
      addToast('Nova versao enviada com sucesso', 'success');
      closeVersionModal();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Erro ao enviar versao';
      setVersionErrors([message]);
      addToast(message, 'error');
    } finally {
      setIsUploadingVersion(false);
    }
  };

  /* ── Detail open ── */

  const openDetail = (id: string): void => {
    setDetailDocId(id);
  };

  const closeDetail = (): void => {
    setDetailDocId(null);
  };

  /* ── Render helpers ── */

  const renderDocumentCard = (doc: Document): ReactNode => (
    <div
      key={doc.id}
      className="doc-card"
      onClick={() => openDetail(doc.id)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => { if (e.key === 'Enter') openDetail(doc.id); }}
    >
      {/* Arquivo e da versao corrente: o documento e o dossie, a versao e o
          arquivo. */}
      <div className="doc-card-icon">{getFileIcon(doc.currentVersion?.mimeType ?? '', 36)}</div>
      <div className="doc-card-body">
        <h3 className="doc-card-title">{doc.title}</h3>
        <div className="doc-card-meta">
          <Badge variant={categoryColors[doc.category] ?? 'neutral'}>
            {categoryLabel[doc.category] ?? doc.category}
          </Badge>
          <span className="doc-card-size">
            {formatFileSize(doc.currentVersion?.fileSize ?? 0)}
          </span>
          <span className="doc-card-version">v{doc.currentVersionNumber ?? 1}</span>
          <span className="doc-card-date">{formatDate(doc.createdAt)}</span>
        </div>
        {doc.tags.length > 0 && (
          <div className="doc-card-tags">
            {doc.tags.slice(0, 3).map((tag) => (
              <span key={tag} className="doc-tag">{tag}</span>
            ))}
            {doc.tags.length > 3 && <span className="doc-tag doc-tag--more">+{doc.tags.length - 3}</span>}
          </div>
        )}
      </div>
      <div className="doc-card-actions">
        <button
          className="doc-action-btn"
          title="Baixar"
          onClick={(e) => {
            e.stopPropagation();
            const url = doc.currentVersion?.downloadUrl ?? doc.versions?.[0]?.downloadUrl;
            if (url) window.open(url, '_blank');
          }}
        >
          <IconDownload size={16} />
        </button>
        <button
          className="doc-action-btn"
          title="Excluir"
          onClick={(e) => {
            e.stopPropagation();
            setDeleteConfirmId(doc.id);
          }}
        >
          <IconTrash2 size={16} />
        </button>
      </div>
    </div>
  );

  const renderSkeletonGrid = (): ReactNode => (
    <div className="doc-grid">
      {Array.from({ length: 6 }, (_, i) => (
        <div key={i} className="doc-card">
          <div className="doc-card-icon">
            <LoadingSkeleton width={36} height={36} borderRadius="8px" />
          </div>
          <div className="doc-card-body">
            <LoadingSkeleton width="70%" height="16px" />
            <div className="doc-card-meta" style={{ marginTop: '0.5rem' }}>
              <LoadingSkeleton width={60} height="20px" borderRadius="10px" />
              <LoadingSkeleton width={50} height="16px" />
              <LoadingSkeleton width={30} height="16px" />
            </div>
          </div>
        </div>
      ))}
    </div>
  );

  /* ── Loading state ── */
  if (isLoading && !data) {
    return (
      <div className="documents-page">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-md mb-lg">
          <div>
            <h1 className="font-display-lg text-display-lg text-on-surface m-0">Documentos</h1>
            <p className="font-body-md text-body-md text-on-surface-variant mt-xs">Documentos armazenados e versões.</p>
          </div>
        </div>
        <Card>{renderSkeletonGrid()}</Card>
      </div>
    );
  }

  /* ── Error state ── */
  if (error) {
    return (
      <div className="documents-page">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-md mb-lg">
          <div>
            <h1 className="font-display-lg text-display-lg text-on-surface m-0">Documentos</h1>
            <p className="font-body-md text-body-md text-on-surface-variant mt-xs">Documentos armazenados e versões.</p>
          </div>
        </div>
        <Card>
          <div className="error-state" role="alert">
            <p>Erro ao carregar documentos: {(error as Error).message}</p>
            <Button variant="primary" onClick={() => window.location.reload()}>
              Tentar novamente
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="documents-page">
      {/* ── Header ── */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-md mb-lg">
        <div>
          <h1 className="font-display-lg text-display-lg text-on-surface m-0">Documentos</h1>
          <p className="font-body-md text-body-md text-on-surface-variant mt-xs">Documentos armazenados e versões.</p>
        </div>
        <div className="flex flex-wrap items-center gap-sm">
          <Button variant="primary" onClick={openUploadModal}>
            <IconUpload size={16} /> Upload
          </Button>
        </div>
      </div>

      {/* ── Filters ── */}
      <Card>
        <div className="filters">
          <Input
            label=""
            placeholder="Buscar por nome ou tag..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
          />
          <Select
            label=""
            value={categoryFilter}
            onChange={(e) => {
              setCategoryFilter(e.target.value);
              setPage(1);
            }}
          >
            {categoryOptions.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </Select>
        </div>

        {/* ── Empty state ── */}
        {documents.length === 0 ? (
          <div className="empty-state">
            <IconFileText size={48} />
            <p>Nenhum documento encontrado</p>
            <Button variant="primary" onClick={openUploadModal}>
              Fazer upload
            </Button>
          </div>
        ) : (
          <>
            {/* ── Grid ── */}
            <div className="doc-grid">
              {documents.map(renderDocumentCard)}
            </div>

            {/* ── Pagination ── */}
            {totalPages > 1 && (
              <div className="pagination">
                <button
                  className="page-btn"
                  disabled={page <= 1}
                  onClick={() => setPage(page - 1)}
                >
                  Anterior
                </button>
                <span className="page-info">
                  Pagina {page} de {totalPages} ({total} documentos)
                </span>
                <button
                  className="page-btn"
                  disabled={page >= totalPages}
                  onClick={() => setPage(page + 1)}
                >
                  Próxima
                </button>
              </div>
            )}
          </>
        )}
      </Card>

      {/* ── Upload Modal ── */}
      <Modal
        isOpen={uploadModalOpen}
        onClose={closeUploadModal}
        title="Upload de documento"
        size="md"
        footer={
          <div className="modal-actions">
            <Button variant="outline" onClick={closeUploadModal} disabled={isUploading}>
              Cancelar
            </Button>
            <Button variant="primary" onClick={handleUpload} loading={isUploading}>
              Enviar
            </Button>
          </div>
        }
      >
        <div className="form-fields">
          <Input
            label="Nome do documento"
            value={uploadForm.name}
            onChange={(e) => setUploadForm((prev) => ({ ...prev, name: e.target.value }))}
            placeholder="Ex: Nota Fiscal 001"
          />
          <Select
            label="Categoria"
            value={uploadForm.category}
            onChange={(e) => setUploadForm((prev) => ({ ...prev, category: e.target.value }))}
          >
            <option value="">Selecione...</option>
            {categoryOptions
              .filter((o) => o.value !== 'ALL')
              .map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
          </Select>
          <Input
            label="Tags (separadas por virgula)"
            value={uploadForm.tags}
            onChange={(e) => setUploadForm((prev) => ({ ...prev, tags: e.target.value }))}
            placeholder="Ex: nf, entrada, janeiro"
          />
          <Input
            label="Empresa (ID)"
            value={uploadForm.companyId}
            onChange={(e) => setUploadForm((prev) => ({ ...prev, companyId: e.target.value }))}
            placeholder="UUID da empresa (opcional)"
          />
          <div className="form-file">
            <label className="form-file-label">Arquivo</label>
            <input
              ref={fileInputRef}
              type="file"
              className="form-file-input"
              onChange={(e) => {
                const file = e.target.files?.[0] ?? null;
                setUploadForm((prev) => ({ ...prev, file }));
              }}
            />
            {uploadForm.file && (
              <span className="form-file-name">
                {uploadForm.file.name} ({formatFileSize(uploadForm.file.size)})
              </span>
            )}
          </div>
          {uploadErrors.length > 0 && (
            <div className="form-errors" role="alert">
              {uploadErrors.map((err, i) => <p key={i}>{err}</p>)}
            </div>
          )}
        </div>
      </Modal>

      {/* ── Edit Modal ── */}
      <Modal
        isOpen={editModalOpen}
        onClose={closeEditModal}
        title="Editar documento"
        size="md"
        footer={
          <div className="modal-actions">
            <Button variant="outline" onClick={closeEditModal} disabled={isSavingEdit}>
              Cancelar
            </Button>
            <Button variant="primary" onClick={handleEditSave} loading={isSavingEdit}>
              Salvar
            </Button>
          </div>
        }
      >
        <div className="form-fields">
          <Input
            label="Nome do documento"
            value={editName}
            onChange={(e) => setEditName(e.target.value)}
            placeholder="Nome do documento"
          />
          <Select
            label="Categoria"
            value={editCategory}
            onChange={(e) => setEditCategory(e.target.value)}
          >
            <option value="">Selecione...</option>
            {categoryOptions
              .filter((o) => o.value !== 'ALL')
              .map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
          </Select>
          <Input
            label="Tags (separadas por virgula)"
            value={editTags}
            onChange={(e) => setEditTags(e.target.value)}
            placeholder="Ex: nf, contrato"
          />
          {editErrors.length > 0 && (
            <div className="form-errors" role="alert">
              {editErrors.map((err, i) => <p key={i}>{err}</p>)}
            </div>
          )}
        </div>
      </Modal>

      {/* ── New Version Modal ── */}
      <Modal
        isOpen={versionModalOpen}
        onClose={closeVersionModal}
        title="Nova versao"
        size="sm"
        footer={
          <div className="modal-actions">
            <Button variant="outline" onClick={closeVersionModal} disabled={isUploadingVersion}>
              Cancelar
            </Button>
            <Button variant="primary" onClick={handleUploadVersion} loading={isUploadingVersion}>
              Enviar versao
            </Button>
          </div>
        }
      >
        <div className="form-fields">
          <div className="form-file">
            <label className="form-file-label">Arquivo</label>
            <input
              ref={versionFileInputRef}
              type="file"
              className="form-file-input"
              onChange={(e) => {
                const file = e.target.files?.[0] ?? null;
                setVersionFile(file);
              }}
            />
            {versionFile && (
              <span className="form-file-name">
                {versionFile.name} ({formatFileSize(versionFile.size)})
              </span>
            )}
          </div>
          <Input
            label="Observações"
            value={versionNotes}
            onChange={(e) => setVersionNotes(e.target.value)}
            placeholder="O que mudou nesta versao? (opcional)"
          />
          {versionErrors.length > 0 && (
            <div className="form-errors" role="alert">
              {versionErrors.map((err, i) => <p key={i}>{err}</p>)}
            </div>
          )}
        </div>
      </Modal>

      {/* ── Detail Modal ── */}
      <Modal
        isOpen={!!detailDocId}
        onClose={closeDetail}
        title={detailDoc?.title ?? 'Detalhes do documento'}
        size="lg"
        footer={
          <div className="modal-actions">
            {detailDoc && (
              <>
                <Button variant="outline" onClick={() => openEditModal(detailDoc)}>
                  Editar
                </Button>
                <Button
                  variant="outline"
                  onClick={() => {
                    closeDetail();
                    if (detailDoc) openVersionModal(detailDoc.id);
                  }}
                >
                  Nova versao
                </Button>
                {deleteConfirmId === detailDoc.id ? (
                  <div className="confirm-delete-inline">
                    <span>Tem certeza?</span>
                    <Button variant="danger" size="sm" onClick={() => handleDelete(detailDoc.id)}>
                      Sim
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => setDeleteConfirmId(null)}>
                      Não
                    </Button>
                  </div>
                ) : (
                  <Button
                    variant="danger"
                    onClick={() => setDeleteConfirmId(detailDoc.id)}
                  >
                    <IconTrash2 size={16} /> Excluir
                  </Button>
                )}
              </>
            )}
          </div>
        }
      >
        {detailLoading && !detailDoc ? (
          <div className="detail-loading">
            <LoadingSkeleton />
            <LoadingSkeleton width="60%" />
            <LoadingSkeleton width="40%" />
          </div>
        ) : detailDoc ? (
          <div className="detail-content">
            {/* Metadados */}
            <div className="detail-section">
              <h3>Informações</h3>
              <div className="detail-grid">
                <div className="detail-field">
                  <span className="detail-label">Título</span>
                  <span className="detail-value">{detailDoc.title}</span>
                </div>
                <div className="detail-field">
                  <span className="detail-label">Categoria</span>
                  <Badge variant={categoryColors[detailDoc.category] ?? 'neutral'}>
                    {categoryLabel[detailDoc.category] ?? detailDoc.category}
                  </Badge>
                </div>
                <div className="detail-field">
                  <span className="detail-label">Tamanho</span>
                  <span className="detail-value">
                    {formatFileSize(detailDoc.currentVersion?.fileSize ?? 0)}
                  </span>
                </div>
                <div className="detail-field">
                  <span className="detail-label">Tipo</span>
                  <span className="detail-value">
                    {detailDoc.currentVersion?.mimeType ?? '—'}
                  </span>
                </div>
                <div className="detail-field">
                  <span className="detail-label">Versao atual</span>
                  <span className="detail-value">v{detailDoc.currentVersionNumber ?? 1}</span>
                </div>
                <div className="detail-field">
                  <span className="detail-label">Criado em</span>
                  <span className="detail-value">{formatDateTime(detailDoc.createdAt)}</span>
                </div>
                <div className="detail-field">
                  <span className="detail-label">Atualizado em</span>
                  <span className="detail-value">{formatDateTime(detailDoc.updatedAt)}</span>
                </div>
                {/* A resposta traz `companyId`, nao o nome da empresa: mostrar
                    o rotulo "Empresa" com um id nao ajuda ninguem, entao no
                    lugar entra o status do documento, que existe. */}
                <div className="detail-field">
                  <span className="detail-label">Status</span>
                  <span className="detail-value">{detailDoc.status}</span>
                </div>
              </div>
              {detailDoc.tags.length > 0 && (
                <div className="detail-tags">
                  <span className="detail-label">Tags</span>
                  <div className="detail-tags-list">
                    {detailDoc.tags.map((tag) => (
                      <span key={tag} className="doc-tag">{tag}</span>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Versoes */}
            <div className="detail-section">
              <h3>Versões ({detailDoc.versions?.length ?? 0})</h3>
              {(detailDoc.versions?.length ?? 0) === 0 ? (
                <p className="detail-empty">Nenhuma versao disponivel</p>
              ) : (
                <div className="versions-list">
                  {(detailDoc.versions ?? []).map((ver) => (
                    <div key={ver.id} className="version-row">
                      <div className="version-info">
                        <span className="version-number">v{ver.version}</span>
                        <span className="version-file">{ver.fileName}</span>
                        <span className="version-size">{formatFileSize(ver.fileSize)}</span>
                        {ver.uploadedByName && (
                          <span className="version-user">{ver.uploadedByName}</span>
                        )}
                        <span className="version-date">{formatDateTime(ver.createdAt)}</span>
                      </div>
                      <div className="version-actions">
                        {ver.notes && <span className="version-notes" title={ver.notes}>{ver.notes}</span>}
                        <a
                          href={ver.downloadUrl}
                          className="version-download"
                          target="_blank"
                          rel="noopener noreferrer"
                          title="Baixar"
                        >
                          <IconDownload size={16} />
                        </a>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Links */}
            {(detailDoc.links?.length ?? 0) > 0 && (
              <div className="detail-section">
                <h3>Vinculos</h3>
                <div className="links-list">
                  {(detailDoc.links ?? []).map((link) => (
                    <div key={link.id} className="link-row">
                      <IconLink size={16} />
                      <span className="link-entity">{link.entityType}</span>
                      {link.entityName && <span className="link-name">{link.entityName}</span>}
                      <span className="link-id">{link.entityId}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        ) : (
          <p className="detail-empty">Documento não encontrado</p>
        )}
      </Modal>

      <style>{`
        .documents-page {
          padding: 1.5rem;
        }
        .filters {
          display: flex;
          gap: 1rem;
          margin-bottom: 1rem;
        }
        .filters > *:first-child {
          flex: 1;
        }

        /* ── Grid ── */
        .doc-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
          gap: 1rem;
        }
        .doc-card {
          display: flex;
          align-items: flex-start;
          gap: 0.75rem;
          padding: 1rem;
          border: 1px solid #eceef0;
          border-radius: 8px;
          background: #ffffff;
          cursor: pointer;
          transition: border-color 0.15s, box-shadow 0.15s;
        }
        .doc-card:hover {
          border-color: #c4c6cf;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
        }
        .doc-card:focus-visible {
          outline: 2px solid #002045;
          outline-offset: 2px;
        }
        .doc-card-icon {
          flex-shrink: 0;
          width: 40px;
          height: 40px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: #f2f4f6;
          border-radius: 8px;
          color: #43474e;
        }
        .doc-card-body {
          flex: 1;
          min-width: 0;
        }
        .doc-card-title {
          margin: 0 0 0.375rem;
          font-size: 0.9375rem;
          font-weight: 600;
          color: #191c1e;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        .doc-card-meta {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          flex-wrap: wrap;
        }
        .doc-card-size,
        .doc-card-version,
        .doc-card-date {
          font-size: 0.75rem;
          color: #74777f;
        }
        .doc-card-tags {
          display: flex;
          gap: 0.25rem;
          margin-top: 0.375rem;
          flex-wrap: wrap;
        }
        .doc-tag {
          display: inline-block;
          padding: 0.125rem 0.5rem;
          font-size: 0.6875rem;
          background: #eceef0;
          color: #43474e;
          border-radius: 4px;
        }
        .doc-tag--more {
          background: transparent;
          color: #74777f;
        }
        .doc-card-actions {
          display: flex;
          flex-direction: column;
          gap: 0.25rem;
          flex-shrink: 0;
        }
        .doc-action-btn {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 28px;
          height: 28px;
          border: none;
          background: transparent;
          border-radius: 4px;
          cursor: pointer;
          color: #74777f;
          transition: background 0.15s, color 0.15s;
        }
        .doc-action-btn:hover {
          background: #eceef0;
          color: #191c1e;
        }

        /* ── Detail modal ── */
        .detail-loading {
          display: flex;
          flex-direction: column;
          gap: 0.75rem;
        }
        .detail-content {
          display: flex;
          flex-direction: column;
          gap: 1.5rem;
        }
        .detail-section h3 {
          margin: 0 0 0.75rem;
          font-size: 0.875rem;
          font-weight: 600;
          color: #43474e;
          text-transform: uppercase;
          letter-spacing: 0.04em;
        }
        .detail-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 0.75rem;
        }
        .detail-field {
          display: flex;
          flex-direction: column;
          gap: 0.25rem;
        }
        .detail-label {
          font-size: 0.75rem;
          color: #74777f;
        }
        .detail-value {
          font-size: 0.875rem;
          color: #191c1e;
        }
        .detail-tags {
          margin-top: 1rem;
        }
        .detail-tags .detail-label {
          display: block;
          margin-bottom: 0.375rem;
        }
        .detail-tags-list {
          display: flex;
          gap: 0.375rem;
          flex-wrap: wrap;
        }
        .detail-empty {
          color: #74777f;
          font-size: 0.875rem;
        }

        /* ── Versions list ── */
        .versions-list {
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
        }
        .version-row {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 0.5rem 0.75rem;
          background: #f2f4f6;
          border-radius: 4px;
          gap: 1rem;
        }
        .version-info {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          flex: 1;
          min-width: 0;
          flex-wrap: wrap;
        }
        .version-number {
          font-weight: 600;
          font-size: 0.8125rem;
          color: #002045;
          flex-shrink: 0;
        }
        .version-file {
          font-size: 0.8125rem;
          color: #191c1e;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        .version-size {
          font-size: 0.75rem;
          color: #74777f;
          flex-shrink: 0;
        }
        .version-user {
          font-size: 0.75rem;
          color: #43474e;
          flex-shrink: 0;
        }
        .version-date {
          font-size: 0.75rem;
          color: #74777f;
          flex-shrink: 0;
        }
        .version-actions {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          flex-shrink: 0;
        }
        .version-notes {
          font-size: 0.75rem;
          color: #74777f;
          max-width: 120px;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        .version-download {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 28px;
          height: 28px;
          border-radius: 4px;
          color: #002045;
          text-decoration: none;
          transition: background 0.15s;
        }
        .version-download:hover {
          background: #d6e3ff;
        }

        /* ── Links ── */
        .links-list {
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
        }
        .link-row {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.5rem 0.75rem;
          background: #f2f4f6;
          border-radius: 4px;
          font-size: 0.8125rem;
          color: #191c1e;
        }
        .link-entity {
          font-weight: 600;
          color: #43474e;
        }
        .link-name {
          color: #002045;
        }
        .link-id {
          color: #74777f;
          font-size: 0.75rem;
        }

        /* ── File input ── */
        .form-file {
          display: flex;
          flex-direction: column;
          gap: 0.375rem;
        }
        .form-file-label {
          font-size: 0.8125rem;
          font-weight: 500;
          color: #191c1e;
        }
        .form-file-input {
          padding: 0.5rem;
          border: 1px solid #c4c6cf;
          border-radius: 4px;
          font-size: 0.875rem;
          font-family: inherit;
          background: #ffffff;
        }
        .form-file-input::file-selector-button {
          padding: 0.25rem 0.75rem;
          border: 1px solid #c4c6cf;
          border-radius: 4px;
          background: #f2f4f6;
          cursor: pointer;
          font-size: 0.8125rem;
          font-family: inherit;
          color: #191c1e;
          margin-right: 0.75rem;
        }
        .form-file-name {
          font-size: 0.8125rem;
          color: #43474e;
        }

        /* ── Shared states ── */
        .error-state {
          padding: 2rem;
          text-align: center;
          color: #ba1a1a;
        }
        .empty-state {
          padding: 3rem 2rem;
          text-align: center;
          color: #74777f;
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 1rem;
        }

        /* ── Pagination ── */
        .pagination {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 1rem;
          padding: 1rem 0;
        }
        .page-btn {
          padding: 0.375rem 0.75rem;
          border: 1px solid #c4c6cf;
          background: #ffffff;
          border-radius: 4px;
          cursor: pointer;
          font-size: 0.875rem;
          font-family: inherit;
        }
        .page-btn:disabled {
          opacity: 0.5;
          cursor: default;
        }
        .page-info {
          font-size: 0.8125rem;
          color: #43474e;
        }

        /* ── Modal shared ── */
        .modal-actions {
          display: flex;
          gap: 0.75rem;
          align-items: center;
        }
        .form-fields {
          display: flex;
          flex-direction: column;
          gap: 1rem;
        }
        .form-errors {
          padding: 0.75rem 1rem;
          background: #ffdad6;
          border: 1px solid #ffb4ab;
          border-radius: 4px;
        }
        .form-errors p {
          color: #ba1a1a;
          font-size: 0.875rem;
          margin: 0.25rem 0;
        }
        .confirm-delete-inline {
          display: flex;
          align-items: center;
          gap: 0.375rem;
          font-size: 0.75rem;
          color: #ba1a1a;
        }
      `}</style>
    </div>
  );
}